import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Modal, message, Input } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '../utils/errorHandler';
import { useLanguage } from '../contexts/LanguageContext';
import { eventBus, EVENTS } from '../utils/eventBus';
import en from '../locales/en';
import zh from '../locales/zh';

const ProfileContainer = styled.div`
  min-height: calc(100vh - 64px);
  background-color: #000;
  color: #fff;
  padding: 40px 20px;
  
  @media (max-width: 768px) {
    min-height: calc(100vh - 56px);
    padding: 32px 16px;
  }
`;

const ProfileCard = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    padding: 32px 24px;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 32px;
  text-align: center;
  background: linear-gradient(120deg, #fff, #888);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 24px;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled.h2`
  font-size: 20px;
  color: #fff;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .anticon {
    color: #1890ff;
  }
`;

const InfoItem = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  margin-bottom: 8px;
`;

const Value = styled.div`
  color: #fff;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.1);
  padding: 12px 16px;
  border-radius: 8px;
  
  @media (max-width: 480px) {
    font-size: 15px;
    padding: 10px 14px;
  }
`;

const ActionButton = styled(Button)`
  height: 40px;
  padding: 0 24px;
  font-size: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: none;
  width: 100%;
  margin-top: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2) !important;
    color: #fff !important;
    transform: translateY(-2px);
  }

  &.ant-btn-primary {
    background: #1890ff;
    
    &:hover {
      background: #40a9ff !important;
    }
  }

  @media (max-width: 768px) {
    height: 36px;
    font-size: 15px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const StyledInput = styled(Input)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  height: 40px;
  color: #fff !important;
  font-size: 16px;
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 15px;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  &:focus,
  &:hover {
    background: rgba(255, 255, 255, 0.15) !important;
    box-shadow: none !important;
  }
`;

const StyledPassword = styled(Input.Password)`
  &.ant-input-affix-wrapper {
    background: rgba(255, 255, 255, 0.1) !important;
    border: none !important;
    height: 40px;
    color: #fff !important;
    
    @media (max-width: 480px) {
      height: 36px;
    }
    
    &:hover,
    &:focus {
      background: rgba(255, 255, 255, 0.15) !important;
      box-shadow: none !important;
    }
    
    .ant-input {
      background: transparent !important;
      color: #fff !important;
      font-size: 16px;
      
      @media (max-width: 480px) {
        font-size: 15px;
      }
      
      &::placeholder {
        color: rgba(255, 255, 255, 0.3);
      }
    }
    
    .ant-input-password-icon {
      color: rgba(255, 255, 255, 0.5);
      
      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }
`;

const CodeInputContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const StyledModalInput = styled(Input)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  height: 40px;
  font-size: 16px;
  
  input {
    background: transparent !important;
    color: #fff !important;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  &:focus,
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    box-shadow: none;
  }
`;

const SendCodeButton = styled(Button)`
  min-width: 100px;
  height: 40px;
  background: #1890ff !important;
  border: none;
  font-size: 14px;
  padding: 4px 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &, span {
    color: #fff !important;
  }

  .ant-btn-loading-icon {
    color: #fff !important;
  }
  
  &:hover {
    background: #40a9ff !important;
    border-color: transparent !important;
  }
  
  &:disabled {
    background: rgba(24, 144, 255, 0.3) !important;
    
    &, span {
      color: rgba(255, 255, 255, 0.45) !important;
    }
  }

  &.ant-btn-loading {
    opacity: 0.8;
    background: #1890ff !important;
    
    &, span {
      color: #fff !important;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: #1a1a1a;
    border-radius: 16px;
    color: #fff;
  }
  
  .ant-modal-header {
    background: transparent;
    border-bottom: none;
    padding: 24px 24px 0;
    
    .ant-modal-title {
      color: #fff;
      font-size: 20px;
      text-align: center;
    }
  }
  
  .ant-modal-body {
    padding: 20px 24px;
    color: #fff;
  }
  
  .ant-modal-footer {
    border-top: none;
    padding: 0 24px 24px;
    
    .ant-btn {
      height: 40px;
      border-radius: 8px;
      font-size: 16px;
      
      &-primary {
        background: #1890ff;
        border: none;
        color: #fff !important;
        
        &:hover {
          background: #40a9ff;
        }
      }
      
      &-default {
        background: rgba(255, 255, 255, 0.1);
        border: none;
        color: #fff !important;
        
        &:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #fff !important;
        }
      }
    }
  }

  p {
    color: #fff !important;
  }
`;

function Profile() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nickname, setNickname] = useState('');
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [loadingNickname, setLoadingNickname] = useState(false);
  const { currentLanguage } = useLanguage();
  const t = currentLanguage === 'en' ? en : zh;
  const [deleteCode, setDeleteCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    fetchNickname();
    
    // 监听认证错误事件
    const handleAuthError = async (error) => {
      if (error.status === 401) {
        await logout();
        navigate('/login');
      }
    };
    
    eventBus.on(EVENTS.AUTH_ERROR, handleAuthError);

  }, []);

  const fetchNickname = async () => {
    try {
      const response = await fetch('https://api.kalimbaflows.com/auth/nickname', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw {
          response: {
            status: response.status,
            data: data
          },
          message: data.message || '获取昵称失败'
        };
      }

      const data = await response.json();
      setNickname(data.nickName || '');
    } catch (error) {
      handleApiError(error, '获取昵称失败');
    }
  };

  const handleUpdateNickname = async () => {
    if (!newNickname.trim()) {
      message.error('昵称不能为空');
      return;
    }

    setLoadingNickname(true);
    try {
      const response = await fetch('https://api.kalimbaflows.com/auth/nickname', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userName: newNickname.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw {
          response: {
            status: response.status,
            data: data
          },
          message: data.message || '更新昵称失败'
        };
      }

      setNickname(newNickname.trim());
      setIsEditingNickname(false);
      message.success('昵称更新成功');
    } catch (error) {
      handleApiError(error, '更新昵称失败');
    } finally {
      setLoadingNickname(false);
    }
  };

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendDeleteCode = async () => {
    try {
      setSendingCode(true);
      const response = await fetch('https://api.kalimbaflows.com/auth/send-delete-code', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw {
          response: {
            status: response.status,
            data: data
          },
          message: data.message || t.common.error
        };
      }

      message.success(t.profile.danger.codeSent);
      startCountdown();
    } catch (error) {
      handleApiError(error, t.common.error);
    } finally {
      setSendingCode(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteCode.trim()) {
      message.error(t.profile.danger.enterCode);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('https://api.kalimbaflows.com/auth/account', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: deleteCode }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw {
          response: {
            status: response.status,
            data: data
          },
          message: data.message || t.common.error
        };
      }

      message.success(t.profile.danger.deleteSuccess);
      setDeleteModalVisible(false);
      await logout();
      navigate('/');
    } catch (error) {
      handleApiError(error, t.common.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProfileContainer>
      <ProfileCard>
        <Title>{t.profile.title}</Title>
        
        <InfoSection>
          <InfoTitle>
            <span>{t.profile.nickname.label}</span>
          </InfoTitle>
          <InfoItem>
            <Value>
              {isEditingNickname ? (
                <>
                  <StyledInput
                    value={newNickname}
                    onChange={(e) => setNewNickname(e.target.value)}
                    placeholder={t.profile.nickname.placeholder}
                    maxLength={20}
                  />
                  <Button 
                    type="primary"
                    size="small"
                    onClick={handleUpdateNickname}
                    loading={loadingNickname}
                  >
                    {t.common.save}
                  </Button>
                  <Button 
                    size="small"
                    onClick={() => {
                      setIsEditingNickname(false);
                      setNewNickname('');
                    }}
                  >
                    {t.common.cancel}
                  </Button>
                </>
              ) : (
                <>
                  <span>{nickname || t.profile.nickname.notSet}</span>
                  <Button 
                    type="link" 
                    onClick={() => {
                      setNewNickname(nickname);
                      setIsEditingNickname(true);
                    }}
                  >
                    {t.common.edit}
                  </Button>
                </>
              )}
            </Value>
          </InfoItem>
        </InfoSection>

        <InfoSection>
          <InfoTitle>
            <span>{t.profile.account.title}</span>
          </InfoTitle>
          <InfoItem>
            <Button type="primary" onClick={() => navigate('/reset-password')}>
              {t.profile.account.changePassword}
            </Button>
          </InfoItem>
        </InfoSection>

        <InfoSection>
          <InfoTitle>
            <span>{t.profile.danger.title}</span>
          </InfoTitle>
          <InfoItem>
            <Button 
              danger 
              type="primary"
              onClick={() => setDeleteModalVisible(true)}
            >
              {t.profile.danger.deleteAccount}
            </Button>
          </InfoItem>
        </InfoSection>
      </ProfileCard>

      <StyledModal
        title={t.profile.danger.deleteConfirm.title}
        open={deleteModalVisible}
        onOk={handleDeleteAccount}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteCode('');
        }}
        confirmLoading={loading}
        okText={t.common.confirm}
        cancelText={t.common.cancel}
      >
        <p>{t.profile.danger.deleteConfirm.content}</p>
        <CodeInputContainer>
          <StyledModalInput
            placeholder={t.profile.danger.codePlaceholder}
            value={deleteCode}
            onChange={(e) => setDeleteCode(e.target.value)}
          />
          <SendCodeButton
            onClick={handleSendDeleteCode}
            disabled={countdown > 0}
            loading={sendingCode}
          >
            {countdown > 0 ? `${countdown}s` : t.profile.danger.sendCode}
          </SendCodeButton>
        </CodeInputContainer>
      </StyledModal>
    </ProfileContainer>
  );
}

export default Profile; 